import type { useMultipartUploadMutations } from "@/features/my-files/components/upload-files-modal/hooks/useMultipartUploadMutations";

const getChunkSize = (fileSize: number) => {
  if (fileSize > 5 * 1024 * 1024 * 1024) return 100 * 1024 * 1024;
  if (fileSize > 1024 * 1024 * 1024) return 50 * 1024 * 1024;

  return 25 * 1024 * 1024;
};

type MultipartMutations = ReturnType<typeof useMultipartUploadMutations>;

export async function uploadMyFileMultipart({
  abortController,
  file,
  mutations,
  onProgress,
  requestId,
}: {
  abortController?: AbortController;
  file: File;
  mutations: MultipartMutations;
  onProgress?: (progress: number) => void;
  requestId: number;
}): Promise<number> {
  const signal = abortController?.signal;

  const { attachmentId, key, uploadId } =
    await mutations.initiateMultipartUploadMutation.mutateAsync({
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      requestId,
    });

  const chunkSize = getChunkSize(file.size);
  const totalParts = Math.ceil(file.size / chunkSize);
  const uploadedParts: { ETag: string; PartNumber: number }[] = [];
  const presignedUrlMap = new Map<number, string>();
  let nextBatchStart = 1;
  let totalBytesUploaded = 0;

  const fetchNextBatch = async (): Promise<void> => {
    if (nextBatchStart > totalParts) return;

    const batchEnd = Math.min(nextBatchStart + 9, totalParts);
    const { presignedUrls } =
      await mutations.getPresignedUrlsForPartsMutation.mutateAsync({
        endPart: batchEnd,
        key,
        startPart: nextBatchStart,
        uploadId,
      });

    for (const { partNumber, presignedUrl } of presignedUrls) {
      presignedUrlMap.set(partNumber, presignedUrl);
    }

    nextBatchStart = batchEnd + 1;
  };

  await fetchNextBatch();

  const uploadPart = async (partNumber: number): Promise<void> => {
    if (signal?.aborted) {
      throw new Error("Upload cancelled");
    }

    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    let presignedUrl = presignedUrlMap.get(partNumber);

    if (!presignedUrl) {
      await fetchNextBatch();
      presignedUrl = presignedUrlMap.get(partNumber);
      if (!presignedUrl) {
        throw new Error(`No presigned URL found for part ${partNumber}`);
      }
    }

    let partBytesUploaded = 0;
    const response = await mutations.uploadChunkMutation.mutateAsync({
      chunk,
      onUploadProgress: (progressEvent) => {
        if (!progressEvent.loaded || !progressEvent.total) return;

        const deltaBytes = progressEvent.loaded - partBytesUploaded;

        totalBytesUploaded += deltaBytes;
        partBytesUploaded = progressEvent.loaded;
        onProgress?.(Math.round((totalBytesUploaded / file.size) * 100));
      },
      presignedUrl,
      signal,
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `Failed to upload part ${partNumber}: ${response.statusText}`,
      );
    }

    const etag = response.headers.etag || response.headers.ETag;

    if (!etag) {
      throw new Error(`No ETag received for part ${partNumber}`);
    }

    uploadedParts.push({
      ETag: etag,
      PartNumber: partNumber,
    });
  };

  const MAX_CONCURRENT_UPLOADS = 3;
  const uploadPromises: Promise<void>[] = [];
  let currentPart = 1;

  const startNextUpload = async (): Promise<void> => {
    if (currentPart > totalParts) return;

    const partToUpload = currentPart++;

    await uploadPart(partToUpload);

    if (currentPart <= totalParts) {
      await startNextUpload();
    }
  };

  for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, totalParts); i++) {
    uploadPromises.push(startNextUpload());
  }

  try {
    await Promise.all(uploadPromises);
    uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

    await mutations.completeMultipartUploadMutation.mutateAsync({
      attachmentId,
      key,
      parts: uploadedParts,
      uploadId,
    });

    onProgress?.(100);

    return attachmentId;
  } catch (error) {
    try {
      await mutations.abortMultipartUploadMutation.mutateAsync({
        attachmentId,
        key,
        uploadId,
      });
    } catch {
      /* best-effort cleanup */
    }
    throw error;
  }
}
